(function () {
  if (customElements.get('image-text-slider')) return;

  class ImageTextSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.image-text-slider__track-inner');
      this.slides = Array.from(this.querySelectorAll('.image-text-slider__slide'));
      this.dots = Array.from(this.querySelectorAll('[data-slider-dot]'));
      this.prevBtn = this.querySelector('[data-slider-prev]');
      this.nextBtn = this.querySelector('[data-slider-next]');
      this.status = this.querySelector('[data-slider-status]');

      this.transition = this.dataset.transition || 'fade';
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = parseInt(this.dataset.speed, 10) || 5000;
      this.loop = this.dataset.loop === 'true';
      this.pauseOnHover = this.dataset.pauseHover === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.index = 0;
      this.timer = null;
      this.pauseReasons = new Set();

      if (this.slides.length < 2) return;

      this.syncInert();
      this.updateStatus();

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.go(this.index - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.go(this.index + 1));
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.go(parseInt(dot.dataset.dotIndex, 10)));
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.go(this.index - 1);
        if (event.key === 'ArrowRight') this.go(this.index + 1);
      });

      if (this.pauseOnHover) {
        this.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse') return;
          this.addPauseReason('hover');
        });
        this.addEventListener('pointerleave', (e) => {
          if (e.pointerType !== 'mouse') return;
          this.removePauseReason('hover');
        });
      }

      this.addEventListener('focusin', () => this.addPauseReason('focus'));
      this.addEventListener('focusout', () => this.removePauseReason('focus'));

      this.handleVisibilityChange = () => {
        if (document.hidden) {
          this.addPauseReason('hidden');
        } else {
          this.removePauseReason('hidden');
        }
      };
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.removePauseReason('offscreen');
          } else {
            this.addPauseReason('offscreen');
          }
        });
      }, { threshold: 0.1 });
      this.intersectionObserver.observe(this);

      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.handleVisibilityChange) {
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      }
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
    }

    addPauseReason(reason) {
      this.pauseReasons.add(reason);
      this.stopAutoplay();
    }

    removePauseReason(reason) {
      this.pauseReasons.delete(reason);
      if (this.pauseReasons.size === 0) this.startAutoplay();
    }

    go(newIndex) {
      const total = this.slides.length;
      if (this.loop) {
        this.index = (newIndex + total) % total;
      } else {
        this.index = Math.min(Math.max(newIndex, 0), total - 1);
      }
      this.render();
      if (this.pauseReasons.size === 0) this.startAutoplay();
    }

    render() {
      if (this.transition === 'slide') {
        this.track.style.transform = 'translateX(-' + this.index * 100 + '%)';
      } else {
        this.slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === this.index);
        });
      }
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.index);
        dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
      });
      this.syncInert();
      this.updateStatus();
    }

    updateStatus() {
      if (!this.status) return;
      this.status.textContent = 'Slide ' + (this.index + 1) + ' of ' + this.slides.length;
    }

    syncInert() {
      this.slides.forEach((slide, i) => {
        slide.toggleAttribute('inert', i !== this.index);
      });
    }

    startAutoplay() {
      if (!this.autoplay || this.reducedMotion) return;
      if (!this.loop && this.index === this.slides.length - 1) return;
      if (this.pauseReasons.size > 0) return;
      this.stopAutoplay();
      this.timer = setInterval(() => this.go(this.index + 1), this.speed);
    }

    stopAutoplay() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }
  }

  customElements.define('image-text-slider', ImageTextSlider);
})();
